import { useMemo, type PropsWithChildren } from "react";
import { createPortal } from "react-dom";

import { styled } from "@linaria/react";
import { FlyingSaucerIcon } from "@phosphor-icons/react";
import { useMedia } from "react-use";

import { withTheme, type PropsWithTheme } from "@gx/dls/theme";

import ErrorLayout from "@/layouts/ErrorLayout";

export const APP_MIN_WIDTH = 720;
export const APP_MIN_HEIGHT = 480;

const SmallScreenWrapperParent = styled.div<{
  $isHidden: boolean;
}>`
  position: relative;

  width: 100vw;
  height: 100vh;

  opacity: ${({ $isHidden }) => ($isHidden ? 0 : 1)};
  pointer-events: ${({ $isHidden }) => ($isHidden ? "none" : "auto")};
`;

const SmallScreenWrapperOverlay = withTheme(styled.div<PropsWithTheme>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  height: 100vh;
  width: 100vw;

  background-color: ${({ theme }) => theme.color.background.primary};
  z-index: 99999;
`);

const SmallScreenWrapper = ({ children }: PropsWithChildren) => {
  const isValidWidth = useMedia(`(min-width: ${APP_MIN_WIDTH}px)`);
  const isValidHeight = useMedia(`(min-height: ${APP_MIN_HEIGHT}px)`);

  const isScreenTooSmall = useMemo(
    () => !isValidWidth || !isValidHeight,
    [isValidWidth, isValidHeight],
  );

  return (
    <>
      <SmallScreenWrapperParent $isHidden={isScreenTooSmall}>{children}</SmallScreenWrapperParent>

      {isScreenTooSmall &&
        createPortal(
          <SmallScreenWrapperOverlay>
            <ErrorLayout
              icon={FlyingSaucerIcon}
              header="We are working hard to support smaller screen sizes"
              message="Please resize your browser window or use a larger screen"
            />
          </SmallScreenWrapperOverlay>,
          document.body,
        )}
    </>
  );
};

export default SmallScreenWrapper;
